/**
 * The time stamp on a review row, in the reference's shape.
 *
 * Measured on shop.app/m/johnelliott: recent reviews read "4 days ago" and
 * "7 days ago"; older ones fall back to an absolute date ("August 12, 2026").
 * The crossover sits at roughly a month, so that is where this switches.
 *
 * Both halves go through `Intl` with the ACTIVE LOCALE, which is the whole
 * point — this storefront runs Arabic and Hebrew, and "4 days ago" hand-built
 * from an English template is exactly the class of bug this codebase keeps
 * finding. `Intl.RelativeTimeFormat` gives "قبل ٤ أيام" and "לפני 4 ימים"
 * for free; where a runtime lacks it, we fall back to the absolute date
 * rather than to English words.
 */

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const RELATIVE_CUTOFF_MS = 30 * DAY;

/** Absolute date in the active locale — the older half of the reference. */
export function formatAbsolute(date, locale) {
  try {
    return date.toLocaleDateString(locale || undefined, {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch (_) {
    return date.toLocaleDateString();
  }
}

/**
 * "4 days ago" for anything inside the cutoff, an absolute date beyond it.
 * Returns '' for a missing or unparseable value so callers can simply omit
 * the element rather than print "Invalid Date".
 */
export function formatReviewTime(value, locale, now = Date.now()) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';

  const elapsed = now - d.getTime();
  // A clock skew (a review "posted" a minute in the future) must not print
  // "in 1 minute"; treat anything not clearly in the past as "just now".
  if (elapsed < MINUTE) {
    const rtf = relativeFormatter(locale);
    return rtf ? rtf.format(0, 'second') : formatAbsolute(d, locale);
  }
  if (elapsed >= RELATIVE_CUTOFF_MS) return formatAbsolute(d, locale);

  const rtf = relativeFormatter(locale);
  if (!rtf) return formatAbsolute(d, locale);

  if (elapsed < HOUR) return rtf.format(-Math.floor(elapsed / MINUTE), 'minute');
  if (elapsed < DAY) return rtf.format(-Math.floor(elapsed / HOUR), 'hour');
  const days = Math.floor(elapsed / DAY);
  if (days < 7) return rtf.format(-days, 'day');
  if (days < 30) return rtf.format(-Math.floor(days / 7), 'week');
  return formatAbsolute(d, locale);
}

function relativeFormatter(locale) {
  if (typeof Intl === 'undefined' || typeof Intl.RelativeTimeFormat !== 'function') return null;
  try {
    return new Intl.RelativeTimeFormat(locale || undefined, { numeric: 'auto' });
  } catch (_) {
    return null;
  }
}
